import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Fab,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  makeStyles,
  Typography,
} from '@material-ui/core';
import {Add, ExpandMore} from '@material-ui/icons';
import classNames from 'classnames';
import {isLoaded} from 'Core/models/Async';
import {List as IList, Map as IMap} from 'immutable';
import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {FormRowGrid, GridRowProps} from 'Quoting/components/shared/GridRow';
import {
  changeSelectedLifeCoverage,
  removeQuoteFormErrorEntries,
  setSelectedCoverageIndex,
  updateSelectedPlan,
} from 'Quoting/ducks/createQuote';
import {CensusClassDTO} from 'Quoting/models/DTOs/Opportunity';
import {
  getByKeyPath,
  getCensusClassURI,
  isA360HealthProduct,
  isQuoteNotEditable,
  safeRenderValue,
  getCoverageNameByCoverageCode,
} from 'Quoting/utils/utilities';
import React, {useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {PlanActionMenu} from './PlanAction';
import {getDeclinedSicCodesForProduct} from './products/rules/rulesValidator';
import {findCoverage} from './utils';
import {CoverageApplicability} from 'Quoting/models/enum';
import {A360ProductModel} from 'Quoting/models/Quoting';
import {validateNumberOfLives} from 'Quoting/utils/validationHelpers';

const useAddPlanStyles = makeStyles(() => ({
  card: {
    boxShadow: 'none',
  },
  cardHeader: {
    borderBottom: '1px solid #cccccc',
    backgroundColor: '#F4F8FC',
  },
  cardTitle: {
    color: '#0033A0',
    fontSize: '1.125rem',
    lineHeight: '1.35rem',
    fontWeight: 600,
  },
  cardContent: {
    borderBottom: '1px solid #cccccc',
    '& .coverage-card': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      paddingLeft: '5vw',
      minHeight: '12vh',
      justifyContent: 'center',
    },
    '& .coverage-class': {
      color: '#2FA0FF',
      border: '1px solid #2FA0FF',
      borderRadius: '4px',
      padding: '5px',
      background: '#ffffff',
      marginTop: '0.5rem',
      fontSize: '0.875rem',
    },
    '& .coverage-name': {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    },
    '& .selected-coverage': {
      '& span': {
        fontSize: '0.875rem',
        fontWeight: 600,
        lineHeight: '1.05rem',
        textAlign: 'left',
        color: '#0033A0',
        borderColor: '#0033A0',
      },
    },
    '& .selected-census-classes': {
      marginTop: '1rem',
    },
    '& .product-name, & .plan-name': {
      fontSize: '0.875rem',
      fontWeight: 600,
      lineHeight: '1.05rem',
      textAlign: 'left',
      color: '#2F3847',
      padding: '1rem 0',
    },
    '& .plan-name': {
      textAlign: 'center',
    },

    '& .add-plan-fab': {
      backgroundColor: '#EDF6FC',
      boxShadow: 'none',
      width: '3rem',
      height: '3rem',

      '& > span > svg': {
        width: '2rem',
        height: '2rem',
      },
    },

    '& .plan-btn': {
      width: '100%',
      padding: '0.85rem 0.25rem',
      '& span': {
        justifyContent: 'flex-start',
        paddingLeft: '0.25rem',
      },
    },

    '& .selected-plan': {
      backgroundColor: '#F4F8FC',
      borderLeft: '4px solid #0033A0',
    },
    padding: '1.5rem 0',
  },
}));

export const SelectPlansSidebar = (props: AddPlanProps) => {
  const {getQuoteFieldValue} = useQuoteField();

  const quotePlans = getQuoteFieldValue<IList<any>>('benefitPackagingDetail.plans');

  return (
    <PlansFormCard title="Select Plans">
      {IList.isList(quotePlans) && quotePlans.size > 0 ? (
        <SelectedPlanList quotePlans={quotePlans} />
      ) : (
        <AddPlan onClickAddPlan={props.onClickAddPlan} selectedProductName={props.selectedProductName ?? ''} />
      )}
    </PlansFormCard>
  );
};

type AddPlanProps = {
  onClickAddPlan: () => void;
  selectedProductName: string;
};

export const AddPlan = ({onClickAddPlan, selectedProductName}: AddPlanProps) => {
  const {getQuoteFieldValue} = useQuoteField();

  const sicCode = getQuoteFieldValue<string>('sicCode');

  const productModel = getQuoteFieldValue('_modelName') as A360ProductModel;

  const numberOfLivesValidationResult = validateNumberOfLives(
    isA360HealthProduct(productModel)
      ? getQuoteFieldValue<number>('participationDetails.numberOfEligibleLives')
      : getQuoteFieldValue<number>('eligibilityInfo.numberOfEligibleLives')
  );

  const isSicCodeDeclined = getDeclinedSicCodesForProduct(productModel).includes(sicCode);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'row-29bf30b8-b1c4-4f34-b740-a83dfc4c58ee'},
      items: [
        {
          key: 'plan-name',
          element: <Typography className="product-name">{safeRenderValue(selectedProductName)}</Typography>,
          props: {xs: 9, sm: 9, md: 9},
        },
        {
          key: 'add-plan-icon',
          element: (
            <Fab
              className="add-plan-fab"
              onClick={onClickAddPlan}
              disabled={Boolean(numberOfLivesValidationResult?.error) || isSicCodeDeclined}
            >
              <Add />
            </Fab>
          ),
          props: {xs: 3, sm: 3, md: 3},
        },
      ],
    },
  ];

  return (
    <Grid container direction="column">
      <FormRowGrid rows={rows} />
      {Boolean(numberOfLivesValidationResult?.error) && (
        <Typography component="span" color="error" style={{maxWidth: '90%'}}>
          {safeRenderValue(numberOfLivesValidationResult?.errorMessage)}
        </Typography>
      )}
      {isSicCodeDeclined && (
        <Typography component="span" color="error" style={{maxWidth: '90%'}}>
          Declined to quote for this industry.
        </Typography>
      )}
    </Grid>
  );
};

export const PlansFormCard = (props: {title: string; children: React.ReactNode}) => {
  const classes = useAddPlanStyles();
  return (
    <Card className={classes.card}>
      <CardHeader title={<span className={classes.cardTitle}>{props.title}</span>} className={classes.cardHeader} />
      <CardContent className={classes.cardContent}>{props.children}</CardContent>
    </Card>
  );
};

const getSelectedCensusClassForCoverage = (
  classGroups: IList<any>,
  coverageGroupLinks: IList<any>,
  customerCensusClasses: CensusClassDTO[]
) => {
  const availableCensusClasses = customerCensusClasses?.reduce((acc, cls) => {
    const censusClassURI = getCensusClassURI(cls?._key?.rootId);
    acc[censusClassURI] = cls?.number;
    return acc;
  }, {} as {[key: string]: string});

  const classGroupsCensusClassURIs = classGroups?.map(cg => ({
    id: getByKeyPath(cg, '_key.id') as string,
    uri: getByKeyPath(cg, 'classGroupExtLink._uri') as string,
  }));

  const censusClassesForCoverage: string[] = [];

  coverageGroupLinks?.forEach(cgl => {
    const classRefInClassGroups = classGroupsCensusClassURIs.find(cg => cg?.id === getByKeyPath(cgl, 'groupLink._ref'));
    if (classRefInClassGroups) {
      censusClassesForCoverage.push(availableCensusClasses[classRefInClassGroups?.uri]);
    }
  });

  return censusClassesForCoverage;
};

const SelectedPlanList = ({quotePlans}: {quotePlans: IList<any>}) => {
  const dispatch = useDispatch();
  const {selectedProduct, selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);

  const {getQuoteFieldValue} = useQuoteField();

  return (
    <div>
      <Typography className="plan-name">{selectedProduct?.name ?? ''}</Typography>
      {isA360HealthProduct(selectedProduct?._model) ? (
        <List component="ul">
          {quotePlans?.map((plan: IMap<string, any>, index: number) => (
            <ListItem
              onClick={() =>
                dispatch(updateSelectedPlan({planKey: getByKeyPath(plan, '_key.id') as string, planIndex: index}))
              }
              className={classNames('product-name', 'plan-btn', index === selectedPlan?.planIndex && 'selected-plan')}
              key={getQuoteFieldValue(`benefitPackagingDetail.plans[${index}]._key.id`)}
            >
              <ListItemText>
                {safeRenderValue(getQuoteFieldValue(`benefitPackagingDetail.plans[${index ?? 0}].planName`))}
              </ListItemText>
              <ListItemSecondaryAction>
                <PlanActionMenu planIndex={index} />
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      ) : (
        <>
          {quotePlans?.map((plan: IMap<string, any>, planIndex: number) => (
            <Accordion
              style={{boxShadow: 'none', border: '1px solid rgba(0, 0, 0, 0.12)'}}
              key={getByKeyPath(plan, '_key.id') as string}
              defaultExpanded
            >
              <AccordionSummary
                style={{display: 'flex', background: 'rgb(245 245 245)', flexDirection: 'row-reverse', height: '5vh'}}
                expandIcon={<ExpandMore style={{color: '#0033A0'}} fontSize="small" />}
              >
                <List style={{width: '20vw', paddingLeft: '2vw'}}>
                  <ListItem key={getQuoteFieldValue(`benefitPackagingDetail.plans[${planIndex}]._key.id`)}>
                    <ListItemText>
                      {safeRenderValue(getQuoteFieldValue(`benefitPackagingDetail.plans[${planIndex ?? 0}].planName`))}
                    </ListItemText>
                    <ListItemSecondaryAction onClick={e => e.stopPropagation()}>
                      <PlanActionMenu planIndex={planIndex} />
                    </ListItemSecondaryAction>
                  </ListItem>
                </List>
              </AccordionSummary>
              <AccordionDetails style={{width: '100%', padding: '0px'}}>
                <List style={{width: '100%', padding: 'unset'}}>
                  {plan?.get('coverageDefs')?.map((coverage: IMap<string, any>, coverageIndex: number) => (
                    <PlanCoverage
                      planIndex={planIndex}
                      plan={plan}
                      coverage={coverage}
                      coverageIndex={coverageIndex}
                      key={getByKeyPath(coverage, '_key.id') as string}
                    />
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          ))}
        </>
      )}
    </div>
  );
};

const getPreviousItemCoverage = (covs: IList<any>, currentIndex: number): string => {
  if (currentIndex <= 0) return covs?.get(0)?.get('code');
  else return covs?.get(currentIndex - 1)?.get('code'); // no previous item
};

type PlanCoverageProps = {
  planIndex: number;
  plan: IMap<string, any>;
  coverageIndex: number;
  coverage: IMap<string, any>;
};

const PlanCoverage = (props: PlanCoverageProps) => {
  const {
    quote,
    selectedProduct,
    selectedPlan,
    selectedLifeCoverage,
    selectedCoverageIndex,
    plansWithCoverages,
    quoteFormErrors,
    customerCensusClasses,
  } = useSelector((state: ReducerState) => state.quoteForm);

  const dispatch = useDispatch();

  const {updateQuoteFieldValue} = useQuoteField();

  const {planIndex, plan, coverageIndex, coverage} = props;

  const classGroups = isLoaded(quote) ? (getByKeyPath(quote.Data, 'classGroups') as IList<any>) : IList<any>();

  const coverageGroupsLinks = coverage?.get('coverageGroupsLinks');

  const coverageCd = coverage?.get('code');

  const planCoverages = plan?.get('coverageDefs');
  const planKey = getByKeyPath(plan, '_key.id') as string;

  const selectedCensusClasses: string[] = useMemo(
    () => getSelectedCensusClassForCoverage(classGroups, coverageGroupsLinks, customerCensusClasses),
    [classGroups, coverageGroupsLinks, customerCensusClasses]
  );

  const allowDeleteCoverage =
    findCoverage(plansWithCoverages, coverageCd, plan?.get('planCd'))?.applicability === CoverageApplicability.Optional;

  const handleCoverageClick = () => {
    dispatch(
      updateSelectedPlan({
        planKey: planKey,
        planIndex: planIndex ?? 0,
      })
    );
    dispatch(changeSelectedLifeCoverage(coverageCd));
    dispatch(setSelectedCoverageIndex(coverageIndex));
  };

  const handleDeleteCoverage = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();

    if (selectedCoverageIndex === coverageIndex) {
      dispatch(changeSelectedLifeCoverage(getPreviousItemCoverage(planCoverages, coverageIndex)));
      dispatch(setSelectedCoverageIndex(coverageIndex - 1));
    }

    updateQuoteFieldValue(
      `benefitPackagingDetail.plans[${planIndex ?? 0}].coverageDefs`,
      planCoverages?.filter((covDef: IMap<string, any>) => covDef?.get('code') !== coverageCd)
    );

    const globalErrorsToRemove = Object.keys(quoteFormErrors)?.filter(
      key => key.includes(coverageCd) && key.includes(planKey)
    );
    dispatch(removeQuoteFormErrorEntries(globalErrorsToRemove));
  };

  return (
    <>
      <ListItem
        onClick={handleCoverageClick}
        className={classNames(
          coverageCd === selectedLifeCoverage && planIndex === selectedPlan?.planIndex && 'selected-plan',
          'coverage-card'
        )}
      >
        <ListItemText
          className={classNames(
            coverageCd === selectedLifeCoverage && planIndex === selectedPlan?.planIndex && 'selected-coverage',
            'coverage-name'
          )}
          primary={getCoverageNameByCoverageCode(selectedProduct?._model, coverageCd)}
          secondary={<SelectedCensusClassesForCoverage selectedCensusClasses={selectedCensusClasses} />}
        />
        {allowDeleteCoverage && (
          <ListItemSecondaryAction>
            <IconButton
              disabled={isQuoteNotEditable(quote)}
              onClick={e => handleDeleteCoverage(e)}
              aria-label="delete coverage"
            >
              <i className="fa fa-trash fa-xs" />
            </IconButton>
          </ListItemSecondaryAction>
        )}
      </ListItem>
      {coverageIndex < plan?.get('coverageDefs')?.size - 1 && <Divider component="li" />}
    </>
  );
};

const SelectedCensusClassesForCoverage = ({selectedCensusClasses}: {selectedCensusClasses: string[]}) =>
  selectedCensusClasses?.length > 0 ? (
    <div className="selected-census-classes">
      {selectedCensusClasses?.map(sc => (
        <span className="coverage-class" key={sc}>
          Class {sc}
        </span>
      ))}
    </div>
  ) : null;
